<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TestInboxAPI from 'dashboard/api/testInbox';
import TestPersonasAPI from 'dashboard/api/testPersonas';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();

const personas = ref([]);
const conversations = ref([]);
const agents = ref([]);
const availableLabels = ref([]);
const selectedConversationId = ref(null);
const selectedPersonaId = ref(null);
const messageDraft = ref('');
const agentDraft = ref('');

const isLoading = ref(false);
const isSending = ref(false);

// Persona form (create/edit)
const editingPersonaId = ref(null);
const showPersonaForm = ref(false);
const emptyPersona = () => ({ name: '', description: '' });
const personaForm = ref(emptyPersona());
const isPersonaEditing = computed(() => Boolean(editingPersonaId.value));

const selectedConversation = computed(() =>
  conversations.value.find(c => c.id === selectedConversationId.value)
);

const alertError = error =>
  useAlert(error?.response?.data?.error || t('TEST_INBOX.ALERTS.ERROR'));

const fetchPersonas = async () => {
  try {
    const { data } = await TestPersonasAPI.get();
    personas.value = data.payload || [];
    if (!selectedPersonaId.value && personas.value.length) {
      selectedPersonaId.value = personas.value[0].id;
    }
  } catch (error) {
    alertError(error);
  }
};

const fetchInbox = async () => {
  isLoading.value = true;
  try {
    const { data } = await TestInboxAPI.show();
    conversations.value = data.payload?.conversations || [];
    agents.value = data.payload?.agents || [];
    availableLabels.value = data.payload?.available_labels || [];
    if (
      selectedConversationId.value &&
      !conversations.value.some(c => c.id === selectedConversationId.value)
    ) {
      selectedConversationId.value = null;
    }
  } catch (error) {
    alertError(error);
  } finally {
    isLoading.value = false;
  }
};

const savePersona = async () => {
  if (!personaForm.value.name.trim()) return;
  try {
    const payload = {
      name: personaForm.value.name.trim(),
      description: personaForm.value.description.trim(),
    };
    if (isPersonaEditing.value) {
      await TestPersonasAPI.update(editingPersonaId.value, payload);
      useAlert(t('TEST_INBOX.ALERTS.PERSONA_UPDATED'));
    } else {
      await TestPersonasAPI.create(payload);
      useAlert(t('TEST_INBOX.ALERTS.PERSONA_CREATED'));
    }
    cancelPersonaForm();
    await fetchPersonas();
  } catch (error) {
    alertError(error);
  }
};

const startPersonaEdit = persona => {
  editingPersonaId.value = persona.id;
  personaForm.value = {
    name: persona.name || '',
    description: persona.description || '',
  };
  showPersonaForm.value = true;
};

const cancelPersonaForm = () => {
  editingPersonaId.value = null;
  personaForm.value = emptyPersona();
  showPersonaForm.value = false;
};

const deletePersona = async persona => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TEST_INBOX.PERSONAS.DELETE_CONFIRM', { name: persona.name })))
    return;
  try {
    await TestPersonasAPI.delete(persona.id);
    if (selectedPersonaId.value === persona.id) selectedPersonaId.value = null;
    useAlert(t('TEST_INBOX.ALERTS.PERSONA_DELETED'));
    await Promise.all([fetchPersonas(), fetchInbox()]);
  } catch (error) {
    alertError(error);
  }
};

const sendMessage = async () => {
  if (!messageDraft.value.trim() || !selectedPersonaId.value || isSending.value)
    return;
  isSending.value = true;
  try {
    const { data } = await TestInboxAPI.sendMessage({
      personaId: selectedPersonaId.value,
      content: messageDraft.value.trim(),
      conversationId: selectedConversationId.value || undefined,
    });
    messageDraft.value = '';
    await fetchInbox();
    // Focus the (possibly new) conversation this message landed in.
    const landedId = data.payload?.conversation_id;
    if (landedId) selectedConversationId.value = landedId;
    useAlert(t('TEST_INBOX.ALERTS.MESSAGE_SENT'));
  } catch (error) {
    alertError(error);
  } finally {
    isSending.value = false;
  }
};

const sendAgentReply = async () => {
  if (!agentDraft.value.trim() || !selectedConversationId.value) return;
  try {
    await TestInboxAPI.reply({
      content: agentDraft.value.trim(),
      conversationId: selectedConversationId.value,
    });
    agentDraft.value = '';
    await fetchInbox();
    useAlert(t('TEST_INBOX.ALERTS.REPLY_SENT'));
  } catch (error) {
    alertError(error);
  }
};

const assignConversation = async event => {
  if (!selectedConversationId.value) return;
  const assigneeId = event.target.value || null;
  try {
    await TestInboxAPI.assign({
      conversationId: selectedConversationId.value,
      assigneeId,
    });
    await fetchInbox();
    useAlert(t('TEST_INBOX.ALERTS.ASSIGNED'));
  } catch (error) {
    alertError(error);
  }
};

const isLabelOn = title =>
  (selectedConversation.value?.label_list || []).includes(title);

const toggleLabel = async title => {
  if (!selectedConversationId.value) return;
  const current = selectedConversation.value?.label_list || [];
  const next = isLabelOn(title)
    ? current.filter(l => l !== title)
    : [...current, title];
  try {
    await TestInboxAPI.updateLabels({
      conversationId: selectedConversationId.value,
      labels: next,
    });
    await fetchInbox();
    useAlert(t('TEST_INBOX.ALERTS.LABELS_UPDATED'));
  } catch (error) {
    alertError(error);
  }
};

const resetSandbox = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TEST_INBOX.HEADER.RESET') + '?')) return;
  try {
    const { data } = await TestInboxAPI.reset();
    selectedConversationId.value = null;
    useAlert(t('TEST_INBOX.ALERTS.RESET_DONE', { count: data.count ?? 0 }));
    await fetchInbox();
  } catch (error) {
    alertError(error);
  }
};

const isIncoming = message => Number(message.message_type) === 0;

const labelFor = conversation =>
  conversation.contact_name || `#${conversation.display_id}`;

onMounted(async () => {
  await Promise.all([fetchPersonas(), fetchInbox()]);
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <!-- Header -->
    <div
      class="flex items-center justify-between gap-4 px-6 py-4 border-b border-n-weak flex-shrink-0"
    >
      <div class="flex flex-col">
        <h1 class="text-lg font-semibold text-n-slate-12 m-0">
          {{ t('TEST_INBOX.HEADER.TITLE') }}
        </h1>
        <span class="text-sm text-n-slate-11 max-w-2xl">
          {{ t('TEST_INBOX.HEADER.SUBTITLE') }}
        </span>
      </div>
      <Button
        :label="t('TEST_INBOX.HEADER.RESET')"
        variant="ghost"
        color="ruby"
        size="sm"
        icon="i-lucide-rotate-ccw"
        @click="resetSandbox"
      />
    </div>

    <div class="flex flex-1 min-h-0">
      <!-- Left: personas + conversations -->
      <div
        class="flex flex-col w-80 flex-shrink-0 border-r border-n-weak overflow-y-auto"
      >
        <!-- Personas -->
        <div class="p-4 border-b border-n-weak">
          <div class="flex items-center justify-between mb-3">
            <span class="text-sm font-semibold text-n-slate-12">
              {{ t('TEST_INBOX.PERSONAS.TITLE') }}
            </span>
            <Button
              :label="t('TEST_INBOX.PERSONAS.NEW')"
              variant="ghost"
              color="blue"
              size="xs"
              icon="i-lucide-plus"
              @click="showPersonaForm = true"
            />
          </div>

          <div
            v-if="showPersonaForm"
            class="flex flex-col gap-2 p-3 mb-3 rounded-lg border border-n-weak bg-n-solid-1"
          >
            <Input
              v-model="personaForm.name"
              :placeholder="t('TEST_INBOX.PERSONAS.NAME_PLACEHOLDER')"
            />
            <Input
              v-model="personaForm.description"
              :placeholder="t('TEST_INBOX.PERSONAS.DESCRIPTION_PLACEHOLDER')"
            />
            <div class="flex justify-end gap-2">
              <Button
                :label="t('TEST_INBOX.PERSONAS.CANCEL')"
                variant="ghost"
                color="slate"
                size="xs"
                @click="cancelPersonaForm"
              />
              <Button
                :label="t('TEST_INBOX.PERSONAS.SAVE')"
                color="blue"
                size="xs"
                :disabled="!personaForm.name.trim()"
                @click="savePersona"
              />
            </div>
          </div>

          <p
            v-if="!personas.length"
            class="text-xs text-n-slate-10 py-2"
          >
            {{ t('TEST_INBOX.PERSONAS.EMPTY') }}
          </p>
          <div v-else class="flex flex-col gap-1">
            <label
              v-for="persona in personas"
              :key="persona.id"
              class="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-n-alpha-black2"
              :class="{ 'bg-n-alpha-2': selectedPersonaId === persona.id }"
            >
              <input
                v-model="selectedPersonaId"
                type="radio"
                :value="persona.id"
                class="size-3.5 accent-n-brand"
              />
              <span class="flex-1 text-sm text-n-slate-12 truncate">
                {{ persona.name }}
              </span>
              <Button
                variant="ghost"
                color="slate"
                size="xs"
                icon="i-lucide-pencil"
                @click.prevent="startPersonaEdit(persona)"
              />
              <Button
                variant="ghost"
                color="ruby"
                size="xs"
                icon="i-lucide-trash-2"
                @click.prevent="deletePersona(persona)"
              />
            </label>
          </div>
        </div>

        <!-- Conversations -->
        <div class="p-4">
          <span class="text-sm font-semibold text-n-slate-12">
            {{ t('TEST_INBOX.CONVERSATIONS.TITLE') }}
          </span>
          <p
            v-if="!conversations.length && !isLoading"
            class="text-xs text-n-slate-10 py-2"
          >
            {{ t('TEST_INBOX.CONVERSATIONS.EMPTY') }}
          </p>
          <div v-else class="flex flex-col gap-1 mt-2">
            <button
              v-for="conversation in conversations"
              :key="conversation.id"
              class="flex items-center gap-2 px-2 py-2 rounded-lg text-left hover:bg-n-alpha-black2"
              :class="{ 'bg-n-alpha-2': selectedConversationId === conversation.id }"
              @click="selectedConversationId = conversation.id"
            >
              <span class="i-lucide-message-square size-4 text-n-slate-10" />
              <span class="flex-1 text-sm text-n-slate-12 truncate">
                {{ labelFor(conversation) }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ conversation.messages?.length || 0 }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Right: thread + composer -->
      <div class="flex flex-col flex-1 min-w-0">
        <!-- Conversation details: assignee + labels -->
        <div
          v-if="selectedConversation"
          class="flex flex-wrap items-center gap-x-6 gap-y-2 px-6 py-3 border-b border-n-weak flex-shrink-0"
        >
          <div class="flex items-center gap-2">
            <span class="text-xs text-n-slate-11">
              {{ t('TEST_INBOX.DETAILS.ASSIGNEE') }}
            </span>
            <select
              :value="selectedConversation.assignee?.id || ''"
              class="h-8 px-2 rounded-lg text-sm bg-n-alpha-black2 text-n-slate-12 border border-n-weak outline-none focus:border-n-brand"
              @change="assignConversation"
            >
              <option value="">{{ t('TEST_INBOX.DETAILS.UNASSIGNED') }}</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </div>
          <div class="flex items-center gap-2 flex-wrap">
            <span class="text-xs text-n-slate-11">
              {{ t('TEST_INBOX.DETAILS.LABELS') }}
            </span>
            <template v-if="availableLabels.length">
              <button
                v-for="label in availableLabels"
                :key="label.title"
                class="px-2 py-0.5 rounded-full text-xs border"
                :class="
                  isLabelOn(label.title)
                    ? 'bg-n-brand text-white border-n-brand'
                    : 'bg-n-alpha-black2 text-n-slate-11 border-n-weak'
                "
                @click="toggleLabel(label.title)"
              >
                {{ label.title }}
              </button>
            </template>
            <span v-else class="text-xs text-n-slate-10">
              {{ t('TEST_INBOX.DETAILS.NO_LABELS') }}
            </span>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-6">
          <p
            v-if="!selectedConversation"
            class="text-sm text-n-slate-10"
          >
            {{ t('TEST_INBOX.CONVERSATIONS.SELECT') }}
          </p>
          <div v-else class="flex flex-col gap-2">
            <div
              v-for="message in selectedConversation.messages"
              :key="message.id"
              class="flex"
              :class="isIncoming(message) ? 'justify-start' : 'justify-end'"
            >
              <div
                class="max-w-[70%] px-3 py-2 rounded-xl text-sm"
                :class="
                  isIncoming(message)
                    ? 'bg-n-alpha-2 text-n-slate-12'
                    : 'bg-n-brand text-white'
                "
              >
                <span
                  v-if="message.sender_name"
                  class="block text-[11px] opacity-70 mb-0.5"
                >
                  {{ message.sender_name }}
                </span>
                {{ message.content }}
              </div>
            </div>
          </div>
        </div>

        <!-- Composer -->
        <div class="flex flex-col gap-2 p-4 border-t border-n-weak flex-shrink-0">
          <div class="flex items-center gap-2">
            <span class="text-xs text-n-slate-11">
              {{ t('TEST_INBOX.COMPOSER.PERSONA_LABEL') }}
            </span>
            <select
              v-model="selectedPersonaId"
              class="h-8 px-2 rounded-lg text-sm bg-n-alpha-black2 text-n-slate-12 border border-n-weak outline-none focus:border-n-brand"
            >
              <option :value="null" disabled>
                {{ t('TEST_INBOX.COMPOSER.PERSONA_PLACEHOLDER') }}
              </option>
              <option
                v-for="persona in personas"
                :key="persona.id"
                :value="persona.id"
              >
                {{ persona.name }}
              </option>
            </select>
          </div>
          <div class="flex items-end gap-2">
            <textarea
              v-model="messageDraft"
              rows="2"
              :placeholder="t('TEST_INBOX.COMPOSER.MESSAGE_PLACEHOLDER')"
              class="flex-1 px-3 py-2 rounded-lg text-sm resize-none bg-n-alpha-black2 text-n-slate-12 border border-n-weak outline-none focus:border-n-brand"
              @keydown.enter.exact.prevent="sendMessage"
            />
            <Button
              :label="t('TEST_INBOX.COMPOSER.SEND')"
              color="blue"
              size="sm"
              icon="i-lucide-send"
              :is-loading="isSending"
              :disabled="!messageDraft.trim() || !selectedPersonaId || isSending"
              @click="sendMessage"
            />
          </div>

          <!-- Reply as agent (only when a conversation is open) -->
          <div
            v-if="selectedConversation"
            class="flex items-end gap-2 pt-2 border-t border-n-weak"
          >
            <textarea
              v-model="agentDraft"
              rows="1"
              :placeholder="t('TEST_INBOX.DETAILS.AGENT_REPLY_PLACEHOLDER')"
              class="flex-1 px-3 py-2 rounded-lg text-sm resize-none bg-n-alpha-black2 text-n-slate-12 border border-n-weak outline-none focus:border-n-brand"
              @keydown.enter.exact.prevent="sendAgentReply"
            />
            <Button
              :label="t('TEST_INBOX.DETAILS.AGENT_REPLY')"
              variant="outline"
              color="slate"
              size="sm"
              icon="i-lucide-reply"
              :disabled="!agentDraft.trim()"
              @click="sendAgentReply"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
